// ArticleA4.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

const THEME = {
  fontFamily:
    "'Prompt', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#0b1220",
  textOnDark: "rgba(255,255,255,.92)",
  glass: "rgba(255,255,255,.08)",
  glassStroke: "rgba(255,255,255,.18)",
  primary: "#60a5fa",
  primary2: "#22d3ee",
};

const META = {
  id: "a4",
  title: "ภาษีหนี้สูญ: ตัดหนี้ลูกหนี้เบี้ยวให้ถูกต้อง",
  category: "ภาษี",
  dateISO: "2025-08-05",
  readMinsFallback: 9,
  hero:
    "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=2000&auto=format&fit=crop",
  excerpt:
    "สรุปแนวคิดหนี้สูญที่ยอมรับทางภาษี เอกสารที่ต้องมี ผลกระทบต่อ VAT/ภาษีเงินได้ ตัวอย่างบันทึกบัญชี และเช็กลิสต์ไทม์ไลน์",
};

export default function ArticleA4() {
  const [progress, setProgress] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [readMins, setReadMins] = useState(META.readMinsFallback);
  const contentRef = useRef(null);
  const heroRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const el = contentRef.current;
      if (!el) return;
      const total = el.scrollHeight - window.innerHeight * 0.6;
      const scrolled = Math.min(
        Math.max(window.scrollY - (el.offsetTop - window.innerHeight * 0.4), 0),
        total
      );
      setProgress(total > 0 ? (scrolled / total) * 100 : 0);

      const h = heroRef.current;
      if (h) {
        const y = window.scrollY;
        h.style.setProperty("--parallax", Math.min(y * 0.22, 120) + "px");
        h.style.setProperty("--blur", Math.min(y / 70, 8) + "px");
        h.style.setProperty("--fade", Math.min(y / 420, 0.5));
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const words = (el.innerText || "").trim().split(/\s+/).length;
    setReadMins(Math.max(5, Math.round(words / 200)));
  }, []);

  useEffect(() => {
    const heads = document.querySelectorAll("main article h2[id], main article h3[id]");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActiveId(e.target.id)),
      { rootMargin: "0px 0px -70% 0px", threshold: 0.01 }
    );
    heads.forEach((h) => io.observe(h));
    return () => io.disconnect();
  }, []);

  const url = useMemo(
    () => (typeof window !== "undefined" ? window.location.href : "/articles/a4"),
    []
  );

  const share = (type) => {
    const text = encodeURIComponent(META.title);
    const u = encodeURIComponent(url);
    let shareUrl = "";
    if (type === "fb") shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    if (type === "tw") shareUrl = `https://twitter.com/intent/tweet?url=${u}&text=${text}`;
    if (type === "ln") shareUrl = `https://social-plugins.line.me/lineit/share?url=${u}`;
    window.open(shareUrl, "_blank", "noopener,noreferrer,width=680,height=520");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert("คัดลอกลิงก์แล้ว");
    } catch {
      alert("คัดลอกไม่สำเร็จ");
    }
  };

  const copyTemplate = (id, label) => {
    const t = document.getElementById(id)?.innerText || "";
    navigator.clipboard
      .writeText(t)
      .then(() => alert(`คัดลอก${label}แล้ว`))
      .catch(() => alert("คัดลอกไม่สำเร็จ"));
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("th-TH", { year: "numeric", month: "short", day: "numeric" });

  return (
    <div className="article-page" style={{ fontFamily: THEME.fontFamily }}>
      <Style />
      <ReadingProgress value={progress} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: META.title,
            datePublished: META.dateISO,
            dateModified: META.dateISO,
            author: { "@type": "Person", name: "ทีมทนายความ & CPA Thai Tax & Law" },
            image: META.hero,
            articleSection: META.category,
            description: META.excerpt,
            mainEntityOfPage: url,
          }),
        }}
      />

      <header className="hero" ref={heroRef}>
        <img className="hero-media" src={META.hero} alt="" />
        <div className="hero-overlay" />
        <div className="hero-content">
          <nav className="breadcrumbs">
            <a href="/">หน้าแรก</a> <span>•</span> <a href="/articles">บทความ</a> <span>•</span>{" "}
            <span aria-current="page">{META.category}</span>
          </nav>
          <h1 className="hero-title">{META.title}</h1>
          <p className="hero-excerpt">{META.excerpt}</p>
          <div className="hero-meta">
            <span>{fmtDate(META.dateISO)}</span>
            <span>•</span>
            <span>{readMins} นาที</span>
            <span className="cat">{META.category}</span>
          </div>
        </div>
        <div className="hero-bottom-fade" />
      </header>

      <div className="containerr">
        <aside className="side tools">
          <div className="tool-card">
            <div className="tool-title">สารบัญ</div>
            <nav className="toc">
              {[
                ["intro", "เริ่มต้น: หนี้สูญคืออะไร"],
                ["when", "หนี้ไหน “ยอมรับทางภาษี” ได้"],
                ["evidence", "เอกสารที่ต้องมี"],
                ["vat", "ผลต่อ VAT (ขายเชื่อ)"],
                ["cit", "ผลต่อภาษีเงินได้"],
                ["entries", "ตัวอย่างบันทึกบัญชี"],
                ["templates", "เอกสารตัวอย่าง (ก๊อปได้)"],
                ["timeline", "ไทม์ไลน์ & เช็กลิสต์"],
                ["faq", "FAQ"],
              ].map(([id, label]) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={activeId === id ? "active" : ""}
                  onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(id);
                if (el) {
                    const yOffset = -80; // ชดเชย navbar 80px
                    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: "smooth" });
                }
                }}

                >
                  {label}
                </a>
              ))}
            </nav>
          </div>

          
        </aside>

        <main>
          <article ref={contentRef}>
            <p className="lede">
              หนี้ที่เก็บไม่ได้ ไม่ใช่แค่ “ขาดทุน” อย่างเดียว — ถ้าดำเนินการถูกขั้นตอน
              อาจ<b>ตัดเป็นค่าใช้จ่ายภาษี</b>ได้ และปรับ VAT ที่เคยนำส่งจากการขายเชื่อได้ด้วย
            </p>

            <Callout type="info">
              ข้อมูลนี้เป็นภาพรวมทั่วไป กฎ/ประกาศอาจปรับเปลี่ยนได้เป็นระยะ
              ก่อนตัดสินใจควรตรวจสอบระเบียบล่าสุดหรือ{" "}
              <a href="/contact">ปรึกษา CPA ของเรา</a>
            </Callout>

            <h2 id="intro">เริ่มต้น: หนี้สูญคืออะไร</h2>
            <ul>
              <li>เกิดจากการขายเชื่อ/ให้กู้/ลูกหนี้การค้า ที่<b>พยายามติดตามอย่างสมควรแล้ว</b>แต่ไม่ได้รับชำระ</li>
              <li>การ “ยอมรับทางภาษี” ต้องมี <b>เหตุและเอกสารรองรับ</b> ตามหลักเกณฑ์ที่ใช้บังคับ</li>
            </ul>

            <h2 id="when">หนี้ไหน “ยอมรับทางภาษี” ได้</h2>
            <div className="grid-2">
              <div>
                <h3>ตัวอย่างเหตุที่พบบ่อย</h3>
                <ul className="check">
                  <li>ลูกหนี้ถูกเลิกกิจการ/ล้มละลาย/ตาย ไม่มีทรัพย์ให้ยึด</li>
                  <li>คดีถึงที่สุดแล้วและบังคับคดีไม่ได้/ยึดไม่พอ</li>
                  <li>หลักฐานยืนยันว่าติดตามต่อไปไม่คุ้มค่าเมื่อเทียบต้นทุน</li>
                </ul>
              </div>
              <div>
                <h3>เงื่อนไขทั่วไป</h3>
                <ul className="check">
                  <li>ต้องมีการติดตามทวงถามอย่างเหมาะสมและมีร่องรอย</li>
                  <li>มีอนุมัติภายในให้ “ตัดหนี้สูญ” พร้อมเหตุผล</li>
                  <li>ทำตามเกณฑ์/ระยะเวลาที่กฎหมายกำหนด ณ ช่วงเวลานั้น</li>
                </ul>
              </div>
            </div>

            <h2 id="evidence">เอกสารที่ต้องมี</h2>
            <ul className="check">
              <li>สัญญา/ใบกำกับ/ใบส่งของ/เอกสารขายเชื่อ</li>
              <li>บันทึกการติดตาม: จดหมายทวงถาม, หลักฐานส่ง, อีเมล/ไลน์, บันทึกโทร</li>
              <li>ผลการดำเนินคดี/คำพิพากษา/เอกสารบังคับคดี (ถ้ามี)</li>
              <li>บันทึกอนุมัติจากผู้มีอำนาจของบริษัท (พร้อมวันที่)</li>
            </ul>

            <h2 id="vat">ผลต่อ VAT (ขายเชื่อ)</h2>
            <p>
              เคยออกใบกำกับและนำส่ง VAT ไปแล้ว แต่เก็บเงินไม่ได้?
              เมื่อเข้าเงื่อนไขหนี้สูญตามระเบียบปัจจุบัน สามารถ<b>ปรับลดภาษีขาย</b>ด้วยเอกสารที่กฎหมายกำหนด
              (เช่น ออกเอกสารปรับลด/ใบเพิ่มหนี้–ใบลดหนี้) ภายในระยะเวลาที่กฎหมายกำหนด
              — รายละเอียดให้ยึดตามกฎล่าสุดของกรมสรรพากร
            </p>

            <h2 id="cit">ผลต่อภาษีเงินได้</h2>
            <ul>
              <li>เมื่อตัดหนี้สูญได้ถูกต้อง สามารถลงเป็น<b>ค่าใช้จ่ายต้องห้าม/ยอมรับได้</b>ตามประเภทกิจการและกฎที่ใช้บังคับ</li>
              <li>ควรแยก “หนี้ปกติ” กับ “หนี้จากการให้กู้ส่วนบุคคล/กรรมการ” เพราะเกณฑ์ยอมรับต่างกัน</li>
              <li>มีผลต่อ<b>ภาษีเงินได้บุคคลธรรมดา/นิติบุคคล</b> และสำรองเผื่อหนี้สงสัยจะสูญ (ถ้าจัดทำ)</li>
            </ul>

            <h2 id="entries">ตัวอย่างบันทึกบัญชี</h2>
            <div className="template">
              <pre>{`เมื่ออนุมัติให้เป็นหนี้สูญ (ขายเชื่อ)
Dr. ค่าเผื่อหนี้สูญ/ค่าใช้จ่ายหนี้สูญ   xxx
    Cr. ลูกหนี้การค้า                       xxx

กรณีเคยนำส่ง VAT จากการขายเชื่อ
Dr. ภาษีขาย (ปรับลดตามระเบียบ)        xx
    Cr. ลูกหนี้การค้า/รายได้                 xx`}</pre>
              <div className="template-actions">
                <span className="ghost">ตัวอย่างเชิงแนวทาง — ต้องปรับให้ตรงมาตรฐานบัญชีของกิจการ</span>
              </div>
            </div>

            <h2 id="templates">เอกสารตัวอย่าง (ก๊อปได้)</h2>
            <div className="template">
              <pre id="memo-template">{`บันทึกขออนุมัติตัดหนี้สูญ

เรื่อง  ขออนุมัติตัดหนี้สูญลูกหนี้การค้า
เรียน  กรรมการผู้จัดการ

ตามที่บริษัทได้ติดตามหนี้จากลูกหนี้ ชื่อ __________ เลขที่ใบกำกับ __________ ยอดหนี้ ______ บาท
โดยมีการทวงถาม/ส่งหนังสือ/ติดต่อหลายครั้ง และพิจารณาแล้วเห็นว่าไม่คุ้มต่อการติดตามต่อไป
จึงขออนุมัติให้ตัดหนี้สูญ พร้อมแนบหลักฐานประกอบตามรายการด้านหลัง

ลงชื่อ __________________ ผู้จัดทำ        วันที่ ___/___/____
ลงชื่อ __________________ ผู้อนุมัติ       วันที่ ___/___/____`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("memo-template", "บันทึกขออนุมัติ")}>คัดลอกบันทึก</button>
              </div>
            </div>

            <div className="template" style={{ marginTop: 12 }}>
              <pre id="letter-template">{`หนังสือแจ้งลูกหนี้กรณีจะตัดหนี้สูญ

วันที่ ___/___/____

เรื่อง แจ้งยอดหนี้คงค้างและกำหนดสุดท้ายก่อนดำเนินการ
เรียน คุณ________________

ตามเอกสารอ้างถึง บริษัทฯ ขอแจ้งยอดคงค้างจำนวน ______ บาท กรุณาชำระภายในวันที่ ___/___/____
หากพ้นกำหนด บริษัทฯ จะดำเนินการตามขั้นตอนทางบัญชี/ภาษี รวมถึงการดำเนินคดีตามสมควร

ขอแสดงความนับถือ
บริษัท __________________
ผู้ติดต่อ __________________ โทร __________________`}</pre>
              <div className="template-actions">
                <button onClick={() => copyTemplate("letter-template", "หนังสือแจ้งลูกหนี้")}>คัดลอกจดหมาย</button>
              </div>
            </div>

            <h2 id="timeline">ไทม์ไลน์ & เช็กลิสต์</h2>
            <ol className="steps">
              <li>รวบรวมสัญญา/ใบกำกับ/หลักฐานส่งมอบ</li>
              <li>ทำบันทึกการติดตามทวงถาม + หลักฐานการส่งหนังสือ</li>
              <li>ประเมินโอกาสฟ้อง/บังคับคดี — คุ้ม/ไม่คุ้ม</li>
              <li>ทำบันทึกเสนออนุมัติ “ตัดหนี้สูญ” แนบหลักฐาน</li>
              <li>ปรับ VAT/บันทึกบัญชี/รายงานผู้บริหาร ตามเกณฑ์ที่ใช้บังคับ</li>
            </ol>

            <h2 id="faq">FAQ</h2>
            <details>
              <summary>ตัดหนี้สูญแล้ว ถ้าลูกหนี้จ่ายคืนภายหลังทำอย่างไร?</summary>
              <p>บันทึกเป็นรายได้อื่นตามมาตรฐานบัญชี และพิจารณาผลทางภาษี/ภาษีมูลค่าเพิ่มที่เกี่ยวข้อง</p>
            </details>
            <details>
              <summary>ต้องฟ้องคดีก่อนเสมอไหมจึงจะตัดหนี้สูญได้?</summary>
              <p>ไม่จำเป็นเสมอไป ขึ้นกับเกณฑ์และพยานหลักฐานว่ามีมูลพอที่จะถือว่าเก็บไม่ได้ตามสมควร</p>
            </details>

            <Callout type="success">
              อยากให้ทีม CPA/ทนายความช่วยทำแพ็กเอกสารหนี้สูญและตรวจผลกระทบภาษี?{" "}
              <a href="/contact">นัดคุยฟรี 15 นาที</a>
            </Callout>

            <div className="end-actions">
              <a className="btn-primary" href="/contact">
                ติดต่อทีม CPA
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path d="M5 12h12M13 5l7 7-7 7" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                className="ghost"
                href="#top"
                onClick={(e) => {e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" });}}
              >
                กลับขึ้นบน
              </a>
            </div>
          </article>

          <section className="related">
            <h3>อ่านต่อ</h3>
            <div className="rel-grid">
              <a className="rel-card" href="/articles/a3">
                <img src="https://images.unsplash.com/photo-1542621334-a254cf47733d?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta"><span className="cat">สัญญา</span><div className="title">สัญญากู้ยืมเงิน: เขียนให้ฟ้องได้</div></div>
              </a>
              <a className="rel-card" href="/articles/a1">
                <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta"><span className="cat">ทวงหนี้</span><div className="title">คู่มือการติดตามทวงหนี้อย่างถูกกฎหมาย</div></div>
              </a>
              <a className="rel-card" href="/articles/a5">
                <img src="https://images.unsplash.com/photo-1506784983877-45594efa4cbe?q=80&w=1200&auto=format&fit=crop" alt="" />
                <div className="meta"><span className="cat">ทวงหนี้</span><div className="title">Demand Letter: จดหมายทวงถาม</div></div>
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}

/* ---------- Small Components ---------- */
function ReadingProgress({ value }) {
  return (
    <div className="progress-wrap" aria-hidden="true">
      <div className="progress" style={{ width: `${value}%` }} />
    </div>
  );
}
function Callout({ type = "info", children }) {
  return <div className={`callout ${type}`}>{children}</div>;
}

/* ---------- Styles ---------- */
function Style() {
  return (
    <style>{`
:root{
  --bg:${THEME.pageBg}; --text:${THEME.textOnDark};
  --glass:${THEME.glass}; --glass-stroke:${THEME.glassStroke};
  --p1:${THEME.primary}; --p2:${THEME.primary2};
}
*{box-sizing:border-box}
.article-page{
  color:var(--text);
  background:
    radial-gradient(1200px 600px at 10% -10%, rgba(14,165,233,.12), transparent 60%),
    radial-gradient(1200px 600px at 90% 10%, rgba(99,102,241,.12), transparent 60%),
    var(--bg);
  min-height:100vh;
}
.progress-wrap{position:fixed;top:0;left:0;right:0;height:3px;z-index:100}
.progress{height:3px;background:linear-gradient(90deg,var(--p1),var(--p2));box-shadow:0 0 16px rgba(34,211,238,.35)}
.hero{position:relative;height:58vh;overflow:hidden;isolation:isolate;--parallax:0px;--blur:0px;--fade:0}
.hero-media{position:absolute;inset:-10% -10% -10% -10%;width:120%;height:120%;object-fit:cover;transform:translateY(var(--parallax));filter:blur(var(--blur)) brightness(.85) saturate(.95)}
.hero-overlay{position:absolute;inset:0;background:
  radial-gradient(700px 380px at 18% 32%, rgba(0,0,0,.45), rgba(0,0,0,.7)),
  linear-gradient(90deg, rgba(0,0,0,.6), rgba(0,0,0,.25), rgba(0,0,0,.6));opacity:calc(1 - var(--fade)*.35)}
.hero-bottom-fade{position:absolute;left:0;right:0;bottom:-1px;height:120px;background:linear-gradient(to bottom, transparent, var(--bg));z-index:2}
.hero-content{position:relative;z-index:3;height:100%;display:grid;align-content:center;gap:14px;padding: clamp(20px,5vw,64px);max-width:1100px;margin:0 auto}
.breadcrumbs{opacity:.9;font-size:.95rem}
.breadcrumbs a{color:rgba(255,255,255,.9);text-decoration:none}
.breadcrumbs span{opacity:.6;margin:0 6px}
.hero-title{font-size: clamp(2rem, 4.2vw, 3.6rem);line-height:1.1;margin:0;font-weight:800}
.hero-excerpt{max-width:60ch;opacity:.98}
.hero-meta{display:flex;gap:8px;align-items:center;opacity:.9}
.hero-meta .cat{margin-left:8px;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.2)}
.containerr{display:grid;grid-template-columns:300px 1fr;gap:24px;max-width:1200px;margin:0 auto;padding:0 clamp(16px,4vw,24px)}
.side{padding-top:28px}
main{padding-top:28px}
.tool-card{position:sticky;top:84px;background:var(--glass);border:1px solid var(--glass-stroke);backdrop-filter: blur(8px);border-radius:16px;padding:14px 14px 12px;margin-bottom:14px}
.tool-title{font-weight:800;margin-bottom:8px}
.toc{display:grid;gap:8px}
.toc a{padding:8px 10px;border-radius:12px;text-decoration:none;color:var(--text);border:1px solid transparent}
.toc a:hover{background:rgba(255,255,255,.08)}
.toc a.active{border-color:rgba(255,255,255,.25);background:linear-gradient(90deg, rgba(96,165,250,.28), rgba(34,211,238,.28))}
.share .share-row{display:grid;gap:8px}
.share button{display:flex;align-items:center;gap:8px;padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);color:var(--text);cursor:pointer}
.share button:hover{background:rgba(255,255,255,.12)}
.author .author-row{display:flex;gap:8px;align-items:center}
.author img{width:40px;height:40px;border-radius:50%;object-fit:cover}
.author .name{font-weight:700}
.author-meta{opacity:.85;margin-top:6px}
article{max-width:760px;margin:0 auto 36px}
.lede{font-size:1.1rem;opacity:.98}
h2{font-size:1.6rem;margin:28px 0 12px;font-weight:900}
h3{font-size:1.15rem;margin:14px 0 6px;font-weight:800}
p{line-height:1.8}
ul{padding-left:18px}
ol{padding-left:18px}
.grid-2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.check li::marker{content:"✔  "}
.callout{border-radius:16px;padding:14px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.06);margin:14px 0}
.callout.info{background:linear-gradient(180deg, rgba(96,165,250,.15), rgba(255,255,255,.06))}
.callout.success{background:linear-gradient(180deg, rgba(52,211,153,.18), rgba(255,255,255,.06))}
.template{border:1px solid var(--glass-stroke);border-radius:16px;overflow:hidden;background:rgba(255,255,255,.04);margin-top:8px}
.template pre{margin:0;padding:14px;line-height:1.7;white-space:pre-wrap}
.template-actions{display:flex;gap:10px;align-items:center;padding:10px;border-top:1px solid var(--glass-stroke)}
.template-actions .ghost{color:var(--text);opacity:.9;text-decoration:none}
.template-actions button{padding:8px 10px;border-radius:12px;border:1px solid var(--glass-stroke);background:rgba(255,255,255,.08);color:var(--text);cursor:pointer}
.template-actions button:hover{background:rgba(255,255,255,.14)}
.end-actions{display:flex;gap:10px;align-items:center;margin:18px 0 6px}
.btn-primary{display:inline-flex;align-items:center;gap:10px;padding:12px 16px;border-radius:999px;color:#fff;text-decoration:none;background:linear-gradient(90deg,var(--p1),var(--p2));border:1px solid rgba(255,255,255,.25)}
.ghost{padding:10px 12px;border-radius:12px;border:1px solid var(--glass-stroke);text-decoration:none;color:var(--text);background:rgba(255,255,255,.06)}
.related{border-top:1px solid rgba(255,255,255,.08);padding-top:20px}
.related h3{font-size:1.2rem;margin:0 0 12px}
.rel-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:14px}
.rel-card{position:relative;overflow:hidden;border-radius:16px;border:1px solid rgba(255,255,255,.12);text-decoration:none;color:#fff;background:#0f1629}
.rel-card img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;filter:brightness(.9);transform:scale(1.02);transition:transform .5s}
.rel-card:hover img{transform:scale(1.06)}
.rel-card .meta{position:relative;padding:12px;background:linear-gradient(180deg, rgba(0,0,0,.0), rgba(0,0,0,.75));margin-top:110px}
.rel-card .cat{display:inline-block;padding:6px 10px;border-radius:999px;background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);margin-bottom:6px}
.rel-card .title{font-weight:800}
@media (max-width:1024px){.containerr{grid-template-columns:1fr}.side{order:2}article{max-width:680px}}
@media (max-width:640px){.hero{height:54vh}.grid-2{grid-template-columns:1fr}.rel-grid{grid-template-columns:1fr}}
    `}</style>
  );
}
